import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import './Admin.css';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [onlineTable, setOnlineTable] = useState([]);
  const { user, loading } = useContext(AuthContext);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      console.error('获取用户失败:', err);
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await axios.get('/admin/applications');
      setApplications(res.data);
    } catch (err) {
      console.error('获取申请失败:', err);
    }
  };

  const fetchAppeals = async () => {
    try {
      const res = await axios.get('/admin/appeals');
      setAppeals(res.data);
    } catch (err) {
      console.error('获取申诉失败:', err);
    }
  };

  const fetchOnlineTable = async () => {
    try {
      const res = await axios.get('/admin/online-table');
      setOnlineTable(res.data);
    } catch (err) {
      console.error('获取在线表格失败:', err);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUsers();
      fetchApplications();
      fetchAppeals();
      fetchOnlineTable();
    }
  }, [user]);

  const handleUpdateOnlineEntry = async (entryId, data) => {
    try {
      await axios.put(`/admin/online-table/${entryId}`, data);
      fetchOnlineTable();
    } catch (err) {
      console.error('更新在线表格失败:', err);
    }
  };

  const handleDeleteOnlineEntry = async (entryId) => {
    if (window.confirm('确定要删除这条记录吗？')) {
      try {
        await axios.delete(`/admin/online-table/${entryId}`);
        fetchOnlineTable();
      } catch (err) {
        console.error('删除失败:', err);
      }
    }
  };

  if (loading) return <div className="loading">加载中...</div>;
  if (!user) return <Navigate to="/account" />;
  if (user.permission !== '腐竹' && user.permission !== '管理') return <Navigate to="/home" />;

  const handleUpdateUser = async (userId, data) => {
    try {
      await axios.put(`/admin/users/${userId}`, data);
      fetchUsers();
    } catch (err) {
      console.error('更新失败:', err);
    }
  };

  const handleReviewApplication = async (appId, status, reviewComment = '') => {
    try {
      await axios.put(`/admin/applications/${appId}`, { status, reviewComment });
      fetchApplications();
      fetchUsers();
    } catch (err) {
      console.error('审核失败:', err);
    }
  };

  const handleReviewAppeal = async (appealId, status, reviewComment = '') => {
    try {
      await axios.put(`/admin/appeals/${appealId}`, { status, reviewComment });
      fetchAppeals();
      fetchUsers();
    } catch (err) {
      console.error('审核失败:', err);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: '待审核', class: 'badge-pending' },
      approved: { text: '已通过', class: 'badge-approved' },
      builder1: { text: '已通过(Builder1)', class: 'badge-builder1' },
      builder2: { text: '已通过(Builder2)', class: 'badge-builder2' },
      rejected: { text: '已拒绝', class: 'badge-rejected' }
    };
    return badges[status] || badges.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  return (
    <div className="admin">
      <h1>管理后台</h1>
      
      <div className="admin-tabs">
        <button onClick={() => setActiveTab('users')} className={activeTab === 'users' ? 'active' : ''}>用户管理</button>
        <button onClick={() => setActiveTab('applications')} className={activeTab === 'applications' ? 'active' : ''}>申请审核</button>
        <button onClick={() => setActiveTab('appeals')} className={activeTab === 'appeals' ? 'active' : ''}>申诉处理</button>
        <button onClick={() => { setActiveTab('online'); fetchOnlineTable(); }} className={activeTab === 'online' ? 'active' : ''}>在线表格</button>
      </div>

      {activeTab === 'users' && (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>游戏ID</th>
                <th>QQ号</th>
                <th>权限</th>
                <th>白名单</th>
                <th>OP</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u._id}>
                  <td>{u.gameId}</td>
                  <td>{u.qqNumber}</td>
                  <td>
                    <select
                      value={u.permission || '访客'}
                      onChange={(e) => handleUpdateUser(u._id, { permission: e.target.value })}
                      disabled={u.permission === '腐竹' && user.permission !== '腐竹'}
                    >
                      <option value="腐竹">腐竹</option>
                      <option value="管理">管理</option>
                      <option value="builder1">Builder1</option>
                      <option value="builder2">Builder2</option>
                      <option value="visitor">Visitor</option>
                      <option value="访客">访客</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={u.whitelisted}
                      onChange={(e) => handleUpdateUser(u._id, { whitelisted: e.target.checked })}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={u.opApproved}
                      onChange={(e) => handleUpdateUser(u._id, { opApproved: e.target.checked })}
                    />
                  </td>
                  <td>
                    <button onClick={() => handleUpdateUser(u._id, { banned: !u.banned })} className={`btn-small ${u.banned ? 'btn-unban' : 'btn-ban'}`}>
                      {u.banned ? '解封' : '封禁'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="applications-list">
          {applications.map((app) => {
            const badge = getStatusBadge(app.status);
            return (
              <div key={app._id} className="application-card">
                <div className="app-header">
                  <h3>{app.type === 'whitelist' ? '白名单申请' : 'OP申请'}</h3>
                  <span className={`badge ${badge.class}`}>{badge.text}</span>
                </div>
                <div className="app-details">
                  <p><strong>申请人:</strong> {app.user?.gameId}</p>
                  <p><strong>QQ号:</strong> {app.qqNumber}</p>
                  <p><strong>群昵称:</strong> {app.groupNickname}</p>
                  <p><strong>游戏ID:</strong> {app.gameId}</p>
                  <p><strong>不破坏承诺:</strong> {app.noGrief}</p>
                  <p><strong>黑名单历史:</strong> {app.blacklistHistory}</p>
                  {app.bilibiliScreenshot && (
                    <p><strong>截图:</strong> <a href={app.bilibiliScreenshot} target="_blank" rel="noopener noreferrer">查看</a></p>
                  )}
                  {app.opFormUrl && (
                    <p><strong>问卷链接:</strong> <a href={app.opFormUrl} target="_blank" rel="noopener noreferrer">查看</a></p>
                  )}
                  <p className="app-date">申请时间: {formatDate(app.createdAt)}</p>
                </div>
                {app.status === 'pending' && (
                  <div className="app-actions">
                    {app.type === 'whitelist' ? (
                      <>
                        <button onClick={() => handleReviewApplication(app._id, 'approved')} className="btn-approve">通过</button>
                        <button onClick={() => {
                          const comment = prompt('请输入拒绝原因:');
                          if (comment !== null) handleReviewApplication(app._id, 'rejected', comment);
                        }} className="btn-reject">拒绝</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => {
                          const comment = prompt('请输入审核意见:');
                          if (comment !== null && comment.trim() !== '') {
                            handleReviewApplication(app._id, 'builder1', comment);
                          } else if (comment !== null) {
                            alert('请输入审核意见');
                          }
                        }} className="btn-builder1">Builder1</button>
                        <button onClick={() => {
                          const comment = prompt('请输入审核意见:');
                          if (comment !== null && comment.trim() !== '') {
                            handleReviewApplication(app._id, 'builder2', comment);
                          } else if (comment !== null) {
                            alert('请输入审核意见');
                          }
                        }} className="btn-builder2">Builder2</button>
                        <button onClick={() => {
                          const comment = prompt('请输入拒绝原因:');
                          if (comment !== null && comment.trim() !== '') {
                            handleReviewApplication(app._id, 'rejected', comment);
                          } else if (comment !== null) {
                            alert('请输入拒绝原因');
                          }
                        }} className="btn-reject">拒绝</button>
                      </>
                    )}
                  </div>
                )}
                {app.reviewComment && (
                  <p className="review-comment"><strong>审核意见:</strong> {app.reviewComment}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'appeals' && (
        <div className="appeals-list">
          {appeals.map((appeal) => {
            const badge = getStatusBadge(appeal.status);
            return (
              <div key={appeal._id} className="appeal-card">
                <div className="appeal-header">
                  <h3>{appeal.type}</h3>
                  <span className={`badge ${badge.class}`}>{badge.text}</span>
                </div>
                <div className="appeal-details">
                  <p><strong>申诉人:</strong> {appeal.user?.gameId}</p>
                  <p><strong>游戏ID:</strong> {appeal.gameId}</p>
                  <p><strong>申诉理由:</strong> {appeal.reason}</p>
                  <p className="appeal-date">申诉时间: {formatDate(appeal.createdAt)}</p>
                </div>
                {appeal.status === 'pending' && (
                  <div className="appeal-actions">
                    <button onClick={() => handleReviewAppeal(appeal._id, 'approved')} className="btn-approve">通过</button>
                    <button onClick={() => {
                      const comment = prompt('请输入拒绝原因:');
                      if (comment !== null) handleReviewAppeal(appeal._id, 'rejected', comment);
                    }} className="btn-reject">拒绝</button>
                  </div>
                )}
                {appeal.reviewComment && (
                  <p className="review-comment"><strong>审核意见:</strong> {appeal.reviewComment}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'online' && (
        <div className="online-table">
          <h2>实时在线表格</h2>
          <table>
            <thead>
              <tr>
                <th>QQ号</th>
                <th>服ID</th>
                <th>游戏昵称</th>
                <th>权限</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {onlineTable.map((entry) => (
                <tr key={entry._id}>
                  <td>
                    <input
                      type="text"
                      value={entry.qqNumber}
                      onChange={(e) => handleUpdateOnlineEntry(entry._id, { qqNumber: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={entry.serverId}
                      onChange={(e) => handleUpdateOnlineEntry(entry._id, { serverId: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={entry.gameNickname}
                      onChange={(e) => handleUpdateOnlineEntry(entry._id, { gameNickname: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      value={entry.permission}
                      onChange={(e) => handleUpdateOnlineEntry(entry._id, { permission: e.target.value })}
                    >
                      <option value="visitor">Visitor</option>
                      <option value="builder1">Builder1</option>
                      <option value="builder2">Builder2</option>
                    </select>
                  </td>
                  <td>
                    <button onClick={() => handleDeleteOnlineEntry(entry._id)} className="btn-delete">删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {onlineTable.length === 0 && (
            <p className="no-data">暂无数据</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Admin;
